import { Component } from '@grafikr/components';
import Cookie from 'js-cookie';

import Modal from 'utils/components/modal';
import { initCountryPicker } from 'utils/languages';
import { NonNullish } from 'utils/nullish-check';

interface GeoJSResponse {
  ip: string;
  name: string;
  country: string;
  country_3: string;
}

export default Component((node, { onMounted }) => {
  const modal = new Modal(node);
  const select = NonNullish(node.querySelector<HTMLSelectElement>('[data-select]'));
  const form = NonNullish(node.querySelector('form'));

  const popupToggles = [...document.querySelectorAll<HTMLElement>('[data-toggle-geo-popup]')];

  const submitHandler = (event: SubmitEvent) => {
    event.preventDefault();

    const selectedOption = select.options[select.selectedIndex];
    const isSubLocale = selectedOption.dataset.isSubLocale !== undefined;

    const selectedLanguage = window.theme.languages.find((language) =>
      isSubLocale
        ? language.subLocales?.find((subLocale) => subLocale.locale === select.value)
        : language.locale === select.value,
    )!;

    let href = selectedLanguage.domain;

    if (isSubLocale) {
      const subLocale = selectedLanguage.subLocales?.find(
        (entry) => entry.locale === select.value,
      )!;
      href += `/${subLocale.locale}`;
    }

    window.location.href = href;
  };

  const setPreferredLanguage = (data: GeoJSResponse) => {
    const fallback = window.theme.languages.find((language) => language.fallback);
    const localeMatch =
      window.theme.languages.find((language) => language.country === data.country) ?? fallback;

    const langWithMatchingSubLocale = window.theme.languages.find((language) =>
      language.subLocales?.find((sublocale) => sublocale.country === data.country),
    );

    const sublocaleMatch = langWithMatchingSubLocale?.subLocales!.find(
      (sublocale) => sublocale.country === data.country,
    );

    const match = sublocaleMatch ?? localeMatch;

    if (match) {
      const optionIndex = Array.from(select.options).findIndex(
        (option) => option.value === match.locale,
      );

      select.selectedIndex = optionIndex;
    }
  };

  const buildSelectors = () => {
    initCountryPicker(select);
  };

  const initModal = () => {
    buildSelectors();
    form.addEventListener('submit', submitHandler);

    modal.open();
  };

  const init = async ({ force }: { force: boolean }) => {
    const response = await fetch('https://get.geojs.io/v1/ip/country.json');

    if (response.ok) {
      const data: GeoJSResponse = await response.json();
      Cookie.set('geo', 'true');

      const fallback = window.theme.languages.find((language) => language.fallback);
      const countryFromLocale = window.theme.languages.find(
        (language) => language.locale === window.theme.request.locale,
        fallback,
      )!;

      if (force || data.country !== countryFromLocale.country) {
        initModal();
        setPreferredLanguage(data);
      }
    }
  };

  const run = () => {
    if (!Cookie.get('geo')) {
      init({ force: false });
    }
  };

  onMounted(() => {
    window.addEventListener('CookieScriptClose', run, false);
    window.addEventListener('CookieScriptAccept', run, false);
    window.addEventListener('CookieScriptAcceptAll', run, false);
    window.addEventListener('CookieScriptReject', run, false);

    if (popupToggles.length) {
      popupToggles.forEach((toggle) => {
        toggle.addEventListener('click', () => init({ force: true }));
      });
    }
  });
});
